// Tipos-base do fio (§14.1). Interfaces = contrato documentado; schemas zod/mini = validação estrutural e fonte do
// JSON Schema da engenharia (contracts:schema). As invariantes semânticas (I-01…I-28) ficam em validate.ts e nos
// validate.*.ts; as do envelope comum (I-02, I-04, I-05, I-17 e I-22), em validate.core.ts.
import * as z from 'zod/mini';

/** ISO-8601 com offset e segundos. Ex.: "2026-10-25T18:44:07-03:00" */
export type Iso = string;
export type UfCode =
  | 'AC'
  | 'AL'
  | 'AM'
  | 'AP'
  | 'BA'
  | 'CE'
  | 'DF'
  | 'ES'
  | 'GO'
  | 'MA'
  | 'MG'
  | 'MS'
  | 'MT'
  | 'PA'
  | 'PB'
  | 'PE'
  | 'PI'
  | 'PR'
  | 'RJ'
  | 'RN'
  | 'RO'
  | 'RR'
  | 'RS'
  | 'SC'
  | 'SE'
  | 'SP'
  | 'TO';
/** ZZ = Exterior (unidade de apuração). */
export type PlaceCode = UfCode | 'ZZ';
export type RegionCode = 'N' | 'NE' | 'CO' | 'SE' | 'S' | 'EX';
/** "13", "22" (2 dígitos no 2º turno). */
export type CandidateNumber = string;
/** /^\d{7}$/ */
export type IbgeCode = string;
/** /^\d{5}$/ (município TSE ou cidade do exterior). */
export type TseCode = string;
export type Office = 'president' | 'governor';
export type ArchiveOffice = 'senate' | 'chamber' | 'assembly';
/**
 * 'archive' = noite do acervo, publicada ou reconstruída (P-26; DR-30). Só vale no alvo replay: fora dele a I-22
 * recusa o arquivo, e a I-17 continua exigindo 'tse' na produção do site ao vivo.
 */
export type DataOrigin = 'tse' | 'simulation' | 'replay' | 'fixture' | 'archive';
/** Anos do acervo (DR-42). */
export type ElectionYear = 2018 | 2022 | 2026;

/**
 * No cabeçalho do feed, `year` é qualquer ano do acervo; a I-02 confere o ano da configuração (2026 no site ao vivo).
 * Os estáticos de 2026 (candidates, first_round) usam ElectionRef<2026> (static.ts).
 */
export interface ElectionRef<Y extends ElectionYear = ElectionYear> {
  year: Y;
  round: 1 | 2;
  /** "2026-10-25": 'AAAA-MM-DD' de Brasília. */
  date: string;
  /** Códigos da eleição no TSE naquele ano; confirmar em ele-c.json (PE-01). Pode vir vazio. */
  tse_election_ids: Partial<Record<Office, string>>;
}
export interface Clocks {
  tse_ht: Iso | null;
  tse_ht_max_uf?: Iso | null;
  collected_at: Iso;
  published_at: Iso;
  verified_at?: Iso;
}
export interface FeedHeader<S extends string> {
  schema: S;
  schema_version: 1;
  election: ElectionRef;
  seq: number;
  epoch: number;
  clocks: Clocks;
  origin: DataOrigin;
}
export interface SourceRef {
  name: string;
  url: string;
  license: string;
  retrieved_at: Iso;
}
/** Pasta da edição em replay/v1/: /^(2018|2022|2026)-[12]t(-[a-z0-9]+)?$/ ("2022-2t", "2026-2t-oficial"). P-01, P-26. */
export type NightKey = string;
/** Arquivo imutável do acervo (P-01, P-26). O cliente nunca monta caminho: lê `path`. */
export interface FileRef {
  /** Relativo à pasta que o contém. */
  path: string;
  /** SHA-256 em hex de 64 dígitos (minúsculos, como o registry_id). */
  sha256: string;
  /** Tamanho sem compressão. */
  bytes?: number;
}
/** [lo99, lo90, q50, hi90, hi99]: faixa de 99%, faixa de 90% e mediana (DR-33). A ordem crescente é invariante. */
export type Band5<T = number> = [T, T, T, T, T];
/** Escopo agregado (P-26, P-27). 'SE' é UF e região: o nível vem sempre explícito, nunca deduzido do código. */
export interface AggregateScope {
  level: 'BR' | 'REGION' | 'UF';
  /** RegionCode com REGION, UfCode com UF; ausente em BR. */
  code?: string;
}

export const UF_CODES = [
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
] as const satisfies readonly UfCode[];
export const PLACE_CODES = [...UF_CODES, 'ZZ'] as const satisfies readonly PlaceCode[];
export const REGION_CODES = ['N', 'NE', 'CO', 'SE', 'S', 'EX'] as const satisfies readonly RegionCode[];
export const DATA_ORIGINS = [
  'tse',
  'simulation',
  'replay',
  'fixture',
  'archive',
] as const satisfies readonly DataOrigin[];
export const ELECTION_YEARS = [2018, 2022, 2026] as const satisfies readonly ElectionYear[];

const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,9})?(Z|[+-]\d{2}:\d{2})$/;

export const IsoSchema = z.string().check(z.regex(ISO_RE));
export const UfCodeSchema = z.enum(UF_CODES);
export const PlaceCodeSchema = z.enum(PLACE_CODES);
export const RegionCodeSchema = z.enum(REGION_CODES);
export const CandidateNumberSchema = z.string().check(z.regex(/^\d{2,5}$/));
export const IbgeCodeSchema = z.string().check(z.regex(/^\d{7}$/));
export const TseCodeSchema = z.string().check(z.regex(/^\d{5}$/));
export const OfficeSchema = z.enum(['president', 'governor']);
export const DataOriginSchema = z.enum(DATA_ORIGINS);
/** Inteiro (os limites de faixa são invariantes, I-06, para devolver E_RANGE). */
export const IntSchema = z.int();
export const CandidatePairSchema = z.tuple([CandidateNumberSchema, CandidateNumberSchema]);

/** Turno, data e ids da eleição: o que o cabeçalho do feed e os estáticos têm em comum; só o ano muda entre eles. */
const electionRefRest = {
  round: z.union([z.literal(1), z.literal(2)]),
  date: z.string().check(z.regex(/^\d{4}-\d{2}-\d{2}$/)),
  tse_election_ids: z.partialRecord(OfficeSchema, z.string()),
};
/** Eleição dos estáticos (candidates, first_round): sempre 2026. */
export const ElectionRefSchema = z.object({ year: z.literal(2026), ...electionRefRest });
/**
 * Eleição do cabeçalho do feed: a mesma, com o ano alargado aos do acervo (P-26). A estrutura aceita os três anos; qual
 * deles vale em cada alvo é invariante (I-02 e I-22, em validate.core.ts), para devolver E_ELECTION e não E_SCHEMA.
 * Dois z.object sobre os mesmos campos, e não z.extend: este módulo está no caminho crítico do site ao vivo, e o
 * z.extend levaria para o JS crítico uma parte do zod que o site não usa (B01, §15.7).
 */
export const HeaderElectionRefSchema = z.object({ year: z.literal(ELECTION_YEARS), ...electionRefRest });

export const ClocksSchema = z.object({
  tse_ht: z.nullable(IsoSchema),
  tse_ht_max_uf: z.optional(z.nullable(IsoSchema)),
  collected_at: IsoSchema,
  published_at: IsoSchema,
  verified_at: z.optional(IsoSchema),
});

export const SourceRefSchema = z.object({
  name: z.string(),
  url: z.string(),
  license: z.string(),
  retrieved_at: IsoSchema,
});

export const NightKeySchema = z.string().check(z.regex(/^(2018|2022|2026)-[12]t(-[a-z0-9]+)?$/));
export const Sha256Schema = z.string().check(z.regex(/^[0-9a-f]{64}$/));
export const FileRefSchema = z.object({
  path: z.string(),
  sha256: Sha256Schema,
  bytes: z.optional(IntSchema),
});
/** Schema de um Band5 de `item`: band5(z.number()) para um valor, band5(z.array(IntSchema)) para colunas. */
export function band5<T extends z.ZodMiniType>(item: T) {
  return z.tuple([item, item, item, item, item]);
}
/** Só a estrutura: o código coerente com o nível (RegionCode, UfCode ou ausente) é conferido por quem valida o arquivo. */
export const AggregateScopeSchema = z.object({
  level: z.enum(['BR', 'REGION', 'UF']),
  code: z.optional(z.string()),
});

/** Campos do envelope comum (P1) para compor os schemas de cada arquivo do feed. */
export function feedHeaderShape<S extends string>(schema: S) {
  return {
    schema: z.literal(schema),
    schema_version: z.literal(1),
    election: HeaderElectionRefSchema,
    seq: IntSchema,
    epoch: IntSchema,
    clocks: ClocksSchema,
    origin: DataOriginSchema,
  };
}

const ELECTION_DAY_UTC_OFFSET_H = 3;

/** Minutos desde 00h00 de Brasília (UTC−3 fixo) do dia da eleição; ≥ 1440 = dia seguinte. */
export function tMin(iso: Iso, electionDate = '2026-10-25'): number {
  const [y, m, d] = electionDate.split('-').map(Number) as [number, number, number];
  return (Date.parse(iso) - Date.UTC(y, m - 1, d, ELECTION_DAY_UTC_OFFSET_H, 0, 0)) / 60_000;
}

/** Inverso de tMin, sempre com offset −03:00 e segundos. */
export function isoAt(tMinValue: number, electionDate = '2026-10-25'): Iso {
  const [y, m, d] = electionDate.split('-').map(Number) as [number, number, number];
  const ms = Date.UTC(y, m - 1, d, ELECTION_DAY_UTC_OFFSET_H, 0, 0) + Math.round(tMinValue * 60_000);
  const local = new Date(ms - ELECTION_DAY_UTC_OFFSET_H * 3_600_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}` +
    `T${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}:${pad(local.getUTCSeconds())}-03:00`
  );
}
